import { useCallback, useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { PageHeader, Empty } from "./ui";
import { GroupDialog } from "./broadcasts/GroupDialog";
import { SendPanel } from "./broadcasts/SendPanel";
import { QuickSend } from "./broadcasts/QuickSend";

export default function Broadcasts() {
  const { api } = useOutletContext();
  const [groups, setGroups] = useState(null);
  const [params] = useSearchParams();
  const [current, setCurrent] = useState(params.get("g"));
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/groups").then((r) => setGroups(r.data)).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const group = groups?.find((g) => g.id === current) || groups?.[0];
  const del = async (g) => {
    if (!window.confirm(`Excluir o grupo “${g.name}”? As mensagens já enviadas continuam nas conversas.`)) return;
    try { await api.delete(`/admin/groups/${g.id}`); toast.success("Grupo excluído."); setCurrent(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Transmissões" description="Envie uma mensagem para várias pessoas de uma vez. Cada uma recebe na própria conversa.">
        <Button data-testid="group-new" onClick={() => setEdit("new")} className="gap-2"><Plus className="h-4 w-4" />Novo grupo</Button>
      </PageHeader>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {groups !== null && (
          <div className="max-w-6xl px-5 pt-5 md:px-8"><QuickSend groups={groups} onReady={(id) => { setCurrent(id); load(); }} /></div>
        )}
        {groups === null ? <Empty>Carregando…</Empty> : groups.length === 0 ? (
          <Empty testId="groups-empty">Nenhum grupo ainda. Use “Enviar para todos”, “Enviar por DDD” ou “Novo grupo”.</Empty>
        ) : (
          <div className="grid max-w-6xl gap-6 px-5 py-6 md:grid-cols-[280px_minmax(0,1fr)] md:px-8">
            <ul data-testid="groups-list" className="space-y-2">
              {groups.map((g) => (
                <li key={g.id}>
                  <button data-testid={`group-${g.id}`} onClick={() => setCurrent(g.id)}
                    className={`flex w-full items-center gap-3 rounded-md border px-3 py-3 text-left transition-colors ${group?.id === g.id ? "border-zap-deep bg-zap-mist" : "border-zap-line hover:bg-zap-mist"}`}>
                    <UsersRound className="h-5 w-5 shrink-0 text-zap-deep" />
                    <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{g.name}</span><span className="text-sm text-muted-foreground">{g.count} {g.count === 1 ? "pessoa" : "pessoas"}</span></span>
                  </button>
                </li>
              ))}
            </ul>
            {group && (
              <div className="min-w-0 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 data-testid="group-title" className="truncate font-display text-lg font-semibold">{group.name}</h2>
                  <div className="flex shrink-0 gap-1">
                    <Button data-testid="group-edit" variant="outline" size="sm" onClick={() => setEdit(group)} className="gap-1"><Pencil className="h-4 w-4" />Editar</Button>
                    <Button data-testid="group-delete" variant="ghost" size="sm" onClick={() => del(group)} className="text-red-700"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <SendPanel group={group} onSent={load} />
              </div>
            )}
          </div>
        )}
      </div>
      <GroupDialog open={!!edit} group={edit === "new" ? null : edit} onClose={() => setEdit(null)}
        onSaved={(g) => { setEdit(null); setCurrent(g.id); load(); }} />
    </div>
  );
}
